import React, { createContext, useContext, useState, useEffect } from 'react';
import {
  auth,
  signInWithGoogle,
  signInWithFacebook,
  signInWithEmail,
  signUpWithEmail,
  signOut as firebaseSignOut
} from '../lib/firebase';
import { onAuthStateChanged } from 'firebase/auth';

const AuthContext = createContext({});

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

// Helper function to link anonymous test results to user account
const linkAnonymousResults = async (firebaseUser) => {
  const sessionId = localStorage.getItem('iqtest_session_id');

  if (!sessionId) {
    console.log('No session ID found to link');
    return;
  }

  try {
    // Extract user info from Firebase user object
    const userId = firebaseUser.uid;
    const userEmail = firebaseUser.email || null;
    const displayName = firebaseUser.displayName || firebaseUser.email?.split('@')[0] || null;

    console.log('Linking anonymous results with:', { userId, userEmail, displayName });

    const response = await fetch('/api/tests/link-results', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        userId,
        sessionId,
        userEmail,
        displayName
      })
    });

    if (response.ok) {
      const result = await response.json();
      console.log('✅ Linked test results to user account:', result.data);

      if (result.data.linkedResults > 0) {
        console.log(`🎉 ${result.data.linkedResults} test result(s) added to your account and leaderboard!`);
      }
    }
  } catch (error) {
    console.error('Failed to link test results:', error);
  }
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isGuest, setIsGuest] = useState(false);

  useEffect(() => {
    // Listen for auth state changes
    const unsubscribe = onAuthStateChanged(auth, async (firebaseUser) => {
      const previousUser = user;
      setUser(firebaseUser);
      setLoading(false);

      if (firebaseUser) {
        setIsGuest(false);

        // Sync user with backend database
        // Only do this if we just logged in (not on every page load)
        if (!previousUser && firebaseUser.uid) {
          console.log('User logged in, syncing with backend...');

          // Get Firebase ID token
          try {
            const idToken = await firebaseUser.getIdToken();

            // Sync with backend
            const response = await fetch('/api/auth/firebase-verify', {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
              },
              body: JSON.stringify({
                idToken,
                profile: {
                  displayName: firebaseUser.displayName,
                  photoURL: firebaseUser.photoURL
                }
              })
            });

            if (response.ok) {
              const result = await response.json();
              console.log('✅ User synced with backend:', result.data);
            } else {
              console.error('Failed to sync user with backend:', await response.text());
            }
          } catch (error) {
            console.error('Error syncing user with backend:', error);
          }

          // Link anonymous test results after backend sync
          console.log('Linking anonymous test results...');
          linkAnonymousResults(firebaseUser);

          // Store user ID in localStorage for test submissions
          localStorage.setItem('user_id', firebaseUser.uid);
        }
      } else {
        // Clear user ID when logged out
        localStorage.removeItem('user_id');
      }
    });

    return unsubscribe;
  }, [user]);

  const continueAsGuest = () => {
    setIsGuest(true);
    setUser(null);
  };

  const login = async (email, password) => {
    setLoading(true);
    try {
      const result = await signInWithEmail(email, password);
      return result;
    } finally {
      setLoading(false);
    }
  };

  const register = async (email, password, displayName) => {
    setLoading(true);
    try {
      const result = await signUpWithEmail(email, password, displayName);
      return result;
    } finally {
      setLoading(false);
    }
  };

  const loginWithGoogle = async () => {
    setLoading(true);
    try {
      const result = await signInWithGoogle();
      return result;
    } finally {
      setLoading(false);
    }
  };

  const loginWithFacebook = async () => {
    setLoading(true);
    try {
      const result = await signInWithFacebook();
      return result;
    } finally {
      setLoading(false);
    }
  };

  const logout = async () => {
    setLoading(true);
    try {
      await firebaseSignOut();
      setUser(null);
      setIsGuest(false);

      // Clear session-related data from localStorage
      localStorage.removeItem('iqtest_session_id');
      localStorage.removeItem('last_result_id');

      console.log('Logged out - cleared session data');
    } finally {
      setLoading(false);
    }
  };

  const value = {
    user,
    loading,
    isGuest,
    continueAsGuest,
    login,
    register,
    loginWithGoogle,
    loginWithFacebook,
    logout,
    isAuthenticated: !!user,
    canSubmitToLeaderboard: !!user,
    userDisplayName: user?.displayName || user?.email || 'Anonymous User',
    userEmail: user?.email,
    userPhoto: user?.photoURL,
    isFirebaseConfigured: true
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
